import { useRef, useEffect } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { getMediaUrl, useGetLesson, useGetLessons, useGetSpace, useUpdateProgress } from '@onesource/services';
import { ContentBlockType, LessonProgressStatus } from '@onesource/schemas';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import { Breadcrumbs, Button } from '@mui/material';
import { useTheme } from '@mui/material';
import DOMPurify from 'dompurify';
import { hasHTML } from '#/utils/has-html';
import { MdDownload } from 'react-icons/md';
import { LuFileText } from 'react-icons/lu';

/**
 * Text Content Route
 * Path: /community-hub/spaces/:id/modules/:moduleId/lessons/:lessonId/text/:contentId
 * Shows a text content block
 */
export const Route = createFileRoute(
  '/_protected/community-hub/spaces/$id/modules/$moduleId/lessons/$lessonId/text/$contentId',
)({
  component: TextContentRoute,
});

function TextContentRoute() {
  const { lessonId, contentId, moduleId, id: spaceId } = Route.useParams();
  const theme = useTheme();

  // Data fetching hooks
  const { data: lesson, isLoading: isLoadingLesson, error: lessonError } = useGetLesson(lessonId);

  const { data: module } = useGetSpace(moduleId);
  const { data: lessons } = useGetLessons(moduleId);
  const findParentLesson = lessons?.find(l => l.id === lesson?.parentId);

  const textBlock = lesson?.contentBlocks?.find(
    block => block.id === contentId && block.blockType === ContentBlockType.TEXT,
  );

  // Track text view on mount via progress update
  const trackedRef = useRef(false);
  const { mutate: updateProgress } = useUpdateProgress(lessonId);
  useEffect(() => {
    if (!trackedRef.current && textBlock) {
      trackedRef.current = true;
      updateProgress({ status: LessonProgressStatus.IN_PROGRESS, lastBlockId: contentId });
    }
  }, [contentId, textBlock, updateProgress]);

  if (isLoadingLesson) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (lessonError) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load content: {lessonError.message}</Alert>
      </Box>
    );
  }

  if (!textBlock) {
    return (
      <Box p={3}>
        <Alert severity="error">Content not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs
        sx={{
          mb: { xs: 2, md: 3 },
          fontFamily: '"Figtree", sans-serif',
          fontSize: { xs: 14, md: 16 },
          lineHeight: { xs: '20px', md: '22px' },
          fontWeight: 400,
          color: theme.palette.grey[600],
          '& .MuiBreadcrumbs-separator': {
            mx: 0.6,
          },
        }}
      >
        {module?.parent && (
          <Link
            to="/community-hub/spaces/$id"
            params={{ id: spaceId }}
            style={{ color: theme.palette.grey[600], textDecoration: 'none' }}
          >
            <Typography
              variant="body2"
              sx={{ mt: hasHTML(module.parent.name) ? -0.5 : 0, fontSize: { xs: 14, md: 16 } }}
            >
              <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(module.parent.name) }} />
            </Typography>
          </Link>
        )}
        {module && (
          <Link
            to="/community-hub/spaces/$id/modules/$moduleId"
            params={{ id: spaceId, moduleId }}
            style={{ color: theme.palette.grey[600], textDecoration: 'none' }}
          >
            <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(module.name) }} />
          </Link>
        )}
        {findParentLesson && (
          <Link
            to="/community-hub/spaces/$id/modules/$moduleId"
            params={{ id: spaceId, moduleId }}
            style={{ color: theme.palette.grey[600], textDecoration: 'none' }}
          >
            {findParentLesson?.title}
          </Link>
        )}
        {lesson && (
          <Link
            to="/community-hub/spaces/$id/modules/$moduleId/lessons/$lessonId"
            params={{ id: spaceId, moduleId, lessonId }}
            style={{ color: theme.palette.grey[600], textDecoration: 'none' }}
          >
            <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(lesson?.title) }} />
          </Link>
        )}
        <Typography component="span" sx={{ fontSize: { xs: 14, md: 16 }, fontWeight: 700 }}>
          Text
        </Typography>
      </Breadcrumbs>

      {/* Download Card */}
      {/* <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 2,
          bgcolor: 'white',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          maxWidth: 500,
          mb: 3,
        }}
      >
        {textBlock.pdfBannerUrl && getMediaUrl(textBlock.pdfBannerUrl) ? (
          <Box
            component="img"
            src={getMediaUrl(textBlock.pdfBannerUrl)}
            alt={textBlock.title || 'Document'}
            sx={{ width: '20%', height: '20%', objectFit: 'cover' }}
          />
        ) : (
          <LuFileText size={48} color={theme.palette.primary.main} />
        )}{' '}
        <Box sx={{ flex: 1 }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            {textBlock.pdfFilename || 'Document.pdf'}
          </Typography>
          {textBlock.pdfPageCount && (
            <Typography variant="caption" color="text.secondary">
              {textBlock.pdfPageCount} pages
              {textBlock.pdfFileSize && ` • ${Math.round(textBlock.pdfFileSize / 1024)} KB`}
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={<MdDownload />}
          href={getMediaUrl(textBlock.pdfUrl) || ''}
          target="_blank"
          rel="noopener noreferrer"
        >
          Download
        </Button>
      </Box> */}

      {/* Text Title */}
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
          {textBlock.title || 'Document'}
        </Typography>
      </Box>

      {/* Text Content */}
      <Box pb={4}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            maxWidth: 900,
            bgcolor: 'white',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {textBlock.bodyHtml ? (
            <Box
              dangerouslySetInnerHTML={{ __html: textBlock.bodyHtml }}
              sx={{
                fontSize: '15px',
                lineHeight: 1.8,
                color: '#333',
                '& p': { mb: 2 },
                '& h1': { fontSize: '2rem', fontWeight: 'bold', mt: 4, mb: 2 },
                '& h2': { fontSize: '1.5rem', fontWeight: 'bold', mt: 3, mb: 2 },
                '& h3': { fontSize: '1.25rem', fontWeight: 'bold', mt: 3, mb: 1.5 },
                '& h4, & h5, & h6': { fontWeight: 'bold', mt: 2, mb: 1 },
                '& ul, & ol': { pl: 4, mb: 2 },
                '& li': { mb: 0.5 },
                '& a': { color: 'primary.main', textDecoration: 'underline' },
                '& blockquote': {
                  borderLeft: '4px solid',
                  borderColor: 'primary.main',
                  pl: 2,
                  ml: 0,
                  my: 2,
                  fontStyle: 'italic',
                  color: 'text.secondary',
                },
                '& code': {
                  bgcolor: 'grey.100',
                  px: 0.5,
                  borderRadius: 0.5,
                  fontFamily: 'monospace',
                  fontSize: '0.9em',
                },
                '& pre': {
                  bgcolor: 'grey.100',
                  p: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                  '& code': {
                    bgcolor: 'transparent',
                    px: 0,
                  },
                },
                '& img': {
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: 1,
                },
                '& table': {
                  width: '100%',
                  borderCollapse: 'collapse',
                  mb: 2,
                  '& th, & td': {
                    border: '1px solid',
                    borderColor: 'divider',
                    p: 1,
                  },
                  '& th': {
                    bgcolor: 'grey.50',
                    fontWeight: 'bold',
                  },
                },
              }}
            />
          ) : (
            <Typography
              variant="body1"
              sx={{
                color: '#333',
                lineHeight: 1.8,
                fontSize: '15px',
                whiteSpace: 'pre-wrap',
              }}
            >
              {textBlock.body || 'No content available.'}
            </Typography>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
